/**
 * Sitewide multi-buy-offer cart sync.
 *
 * The multi-buy-offer widget (sections/multi-buy-offer.liquid) stamps
 * hidden "_mbo_*" line item properties on everything it adds, and
 * cart.ajax.liquid groups the cart display by "_mbo_offer". Items added
 * through any OTHER add-to-cart path on the site (PDP form, quick-shop
 * modal, home page featured product, sticky bar, etc.) know nothing about
 * that and add a plain, ungrouped line - even for the exact same product
 * that already has an active "capsule" sitting in the cart, or that would
 * start one on its own.
 *
 * This works in two layers:
 *  1. A fetch() wrapper that inspects every plain single-item POST to
 *     cart/add.js BEFORE it's sent, and - if the variant belongs to a
 *     collection using the collection-offers template - injects the right
 *     _mbo_* properties into that SAME request. This is what makes the
 *     item land in the cart already grouped on its very first render,
 *     with no flash from "ungrouped" to "grouped".
 *  2. A best-effort post-hoc reconcile() pass (on ajaxProduct:added /
 *     cart:updated) that fixes up anything the wrapper couldn't catch -
 *     e.g. an item already sitting in the cart from before this shipped.
 *
 * Deliberately outside multi-buy-offer.js: that file only loads on pages
 * that render the <multi-buy-offer> section, but this has to run on every
 * page (PDP, home page, etc.) since that's exactly where the gap is.
 */
(function () {
  if (window.MboCartSync) return;
  window.MboCartSync = true;

  var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var collectionVariantIdCache = new Map();
  var syncing = false;
  var queued = false;

  function fetchJson(url, options) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, options || {})).then(function (response) {
      if (!response.ok) throw new Error('Request failed: ' + url);
      return response.json();
    });
  }

  function fetchCart() {
    return fetchJson(rootPath + 'cart.js?t=' + Date.now());
  }

  // Collections behind a multi-buy offer are small, curated capsules, so
  // the first 250 products (Shopify's max per page on this endpoint) is
  // expected to cover them in full. Results are cached per handle for the
  // life of the page so repeat cart changes don't refetch the same list.
  // Keyed by variant id (not product id) since that's all a plain
  // cart/add.js request has to go on before the item actually exists as a
  // cart line.
  function fetchCollectionVariantIds(handle) {
    if (collectionVariantIdCache.has(handle)) {
      return collectionVariantIdCache.get(handle);
    }

    var promise = fetchJson(rootPath + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=250')
      .then(function (data) {
        var variantIds = new Set();
        (data.products || []).forEach(function (product) {
          (product.variants || []).forEach(function (variant) {
            variantIds.add(variant.id);
          });
        });
        return variantIds;
      })
      .catch(function () {
        return new Set();
      });

    collectionVariantIdCache.set(handle, promise);
    return promise;
  }

  function extractMboProperties(properties) {
    var mboProperties = {};
    Object.keys(properties || {}).forEach(function (key) {
      if (key.indexOf('_mbo_') === 0) mboProperties[key] = properties[key];
    });
    return mboProperties;
  }

  // Reads the sitewide manifest theme.liquid renders on every page: which
  // collections use the collection-offers template, and the shared "Cart
  // grouping" recipe (Theme settings > Cart), so a brand new capsule can be
  // built even when nothing from that collection has ever gone through the
  // multi-buy-offer widget in this cart yet.
  function readManifest() {
    var el = document.querySelector('[data-mbo-offer-collections]');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  function buildFreshCapsuleProperties(collection, recipe) {
    return {
      _mbo_offer: 'mbo-' + collection.id,
      _mbo_collection_id: String(collection.id),
      _mbo_collection_handle: collection.handle,
      _mbo_set_size: String(collection.setSize),
      _mbo_set_price: String(recipe.setPriceCents),
      _mbo_bg: recipe.bgColor,
      _mbo_msg_first: recipe.messageFirst,
      _mbo_msg_repeat: recipe.messageRepeat,
      _mbo_msg_singular: recipe.messageSingular,
      _mbo_msg_plural: recipe.messagePlural,
      _mbo_redirect: collection.url
    };
  }

  // Candidate capsules for a given cart snapshot: one entry per already
  // -active capsule (reusing its exact stored properties), plus one per
  // manifest-eligible collection that doesn't have an active capsule yet
  // (built fresh from the shared recipe). Shared by both the pre-add
  // lookup and the post-hoc reconcile pass so they can never disagree.
  function buildCapsuleCandidates(cart, manifest) {
    var capsules = [];
    var seenOfferKeys = new Set();
    var seenCollectionIds = new Set();

    (cart.items || []).forEach(function (item) {
      var properties = item.properties || {};
      var offerKey = properties._mbo_offer;
      var handle = properties._mbo_collection_handle;
      var collectionId = properties._mbo_collection_id;

      if (offerKey && handle && !seenOfferKeys.has(offerKey)) {
        seenOfferKeys.add(offerKey);
        if (collectionId) seenCollectionIds.add(String(collectionId));
        capsules.push({ handle: handle, properties: extractMboProperties(properties) });
      }
    });

    if (manifest && manifest.recipe && Array.isArray(manifest.collections)) {
      manifest.collections.forEach(function (collection) {
        if (seenCollectionIds.has(String(collection.id))) return;
        capsules.push({
          handle: collection.handle,
          properties: buildFreshCapsuleProperties(collection, manifest.recipe)
        });
      });
    }

    return capsules;
  }

  // Finds which capsule (if any) a variant id should join, checking each
  // candidate's collection membership in turn. Used both before an add
  // (to shape the outgoing request) and after (to fix up anything missed).
  async function resolveCapsuleForVariant(variantId, capsules) {
    for (var i = 0; i < capsules.length; i++) {
      var capsule = capsules[i];
      var variantIds = await fetchCollectionVariantIds(capsule.handle);

      if (variantIds.has(variantId)) {
        return capsule.properties;
      }
    }

    return null;
  }

  function joinCapsule(item, mboProperties) {
    // /cart/change.js REPLACES a line's whole properties object rather than
    // merging into it, so the item's own existing properties (a
    // personalization note, engraving text, anything an app or the
    // product form already attached) have to be carried forward here -
    // otherwise this "fix" would silently delete them.
    var mergedProperties = Object.assign({}, item.properties || {}, mboProperties);

    return fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: mergedProperties
      })
    }).catch(function () {
      /* best-effort - the item just stays a normal, ungrouped cart line */
    });
  }

  async function reconcile() {
    if (syncing) {
      queued = true;
      return;
    }

    syncing = true;

    try {
      var manifest = readManifest();
      var cart = await fetchCart();
      var items = cart.items || [];
      var capsules = buildCapsuleCandidates(cart, manifest);

      if (!capsules.length) return;

      var orphanItems = items.filter(function (item) {
        return !(item.properties && item.properties._mbo_offer);
      });

      if (!orphanItems.length) return;

      var joinedAny = false;

      for (var i = 0; i < orphanItems.length; i++) {
        var item = orphanItems[i];
        var properties = await resolveCapsuleForVariant(item.variant_id, capsules);

        if (properties) {
          await joinCapsule(item, properties);
          joinedAny = true;
        }
      }

      // Re-announce the cart so anything already listening for changes
      // (cart drawer markup, upsell refresh, etc.) picks up the corrected
      // grouping - mirrors how the rest of the cart already signals
      // updates, nothing new is invented here. In the normal case this is
      // a no-op safety net: the fetch wrapper below already gets items
      // grouped on their first request, so there's nothing left to fix.
      if (joinedAny) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      }
    } catch (error) {
      /* best-effort background sync - never breaks the cart itself */
    } finally {
      syncing = false;

      if (queued) {
        queued = false;
        reconcile();
      }
    }
  }

  document.addEventListener('ajaxProduct:added', reconcile);
  document.addEventListener('cart:updated', reconcile);

  // Pre-add interception: every add-to-cart control on this theme (PDP
  // form, quick-shop modal, home page product-form quick-buy) ends up
  // calling fetch() with a FormData body against cart/add.js, so wrapping
  // fetch once here covers all of them without touching any of those
  // templates or theme.js itself. Only a plain single-variant FormData
  // request with no properties of its own is touched - a request that
  // already set its own _mbo_* properties (the widget's own add, or the
  // cart upsell card after its own match) is left completely alone.
  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartAdd = url.indexOf('cart/add') !== -1;

    if (!isCartAdd || !init || !(init.body instanceof FormData)) {
      return nativeFetch(input, init);
    }

    var formData = init.body;
    var variantId = formData.get('id');

    if (!variantId || formData.has('properties[_mbo_offer]')) {
      return nativeFetch(input, init);
    }

    variantId = parseInt(variantId, 10);

    return (async function () {
      try {
        var manifest = readManifest();

        if (!manifest || !Array.isArray(manifest.collections) || !manifest.collections.length) {
          return nativeFetch(input, init);
        }

        var cart = await fetchCart();
        var capsules = buildCapsuleCandidates(cart, manifest);
        var properties = await resolveCapsuleForVariant(variantId, capsules);

        if (properties) {
          Object.keys(properties).forEach(function (key) {
            formData.set('properties[' + key + ']', properties[key]);
          });
        }
      } catch (error) {
        /* best-effort - fall through and add the item normally */
      }

      return nativeFetch(input, init);
    })();
  };
})();
